import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Pencil, Trash2, FolderPlus, GraduationCap, PencilRuler,
  BarChart3, Globe, Eye, EyeOff, ImagePlus, Loader2, Check, ChevronsUpDown, Download,
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { saveOrShareBlob } from '@/lib/nativeFile';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useConfirm } from '@/hooks/useConfirm';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  treinamentoCategoriaService, treinamentoCursoService, treinamentoModuloService,
} from '@/services/treinamentosApi';
import { empresaService } from '@/services/api';
import { TreinamentoCategoria, TreinamentoCurso, TreinamentoRelatorio, Empresa } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

export default function GerenciarTreinamentos() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.tipo === 'superadmin';
  const [categorias, setCategorias] = useState<TreinamentoCategoria[]>([]);
  const [loading, setLoading] = useState(true);

  // categoria dialog
  const [catDialog, setCatDialog] = useState<{ open: boolean; edit?: TreinamentoCategoria }>({ open: false });
  const [catNome, setCatNome] = useState('');
  const [catGlobal, setCatGlobal] = useState(false);
  const [catEmpresaId, setCatEmpresaId] = useState('');
  const [empresas, setEmpresas] = useState<Empresa[]>([]);

  // curso dialog (1 passo)
  const [cursoDialog, setCursoDialog] = useState<{ open: boolean; edit?: TreinamentoCurso }>({ open: false });
  const [cursoForm, setCursoForm] = useState({ titulo: '', descricao: '', categoriaId: '', novaCategoria: '' });
  const [salvandoCurso, setSalvandoCurso] = useState(false);

  const [relatorio, setRelatorio] = useState<TreinamentoRelatorio | null>(null);
  const { confirmState, confirmar, fechar: fecharConfirm } = useConfirm();
  const [capaUploading, setCapaUploading] = useState<string | null>(null);
  const capaInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const carregar = useCallback(async () => {
    try {
      const data = await treinamentoCursoService.catalogo();
      setCategorias(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    if (isSuperAdmin) {
      empresaService.getAll().then((r) => r.success && setEmpresas(r.data)).catch(() => {});
    }
  }, [carregar, isSuperAdmin]);

  // ─── Categoria ───
  const abrirCatDialog = (edit?: TreinamentoCategoria) => {
    setCatNome(edit?.nome ?? '');
    setCatGlobal(edit ? edit.empresa_id === null : false);
    setCatEmpresaId(edit?.empresa_id ?? '');
    setCatDialog({ open: true, edit });
  };
  const salvarCategoria = async () => {
    if (!catNome.trim()) return;
    try {
      if (catDialog.edit) {
        await treinamentoCategoriaService.update(catDialog.edit.id, { nome: catNome.trim() });
      } else {
        await treinamentoCategoriaService.create({
          nome: catNome.trim(),
          global: isSuperAdmin && catGlobal,
          empresa_id: isSuperAdmin && !catGlobal && catEmpresaId ? catEmpresaId : undefined,
        });
      }
      setCatDialog({ open: false });
      toast.success('Categoria salva');
      carregar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao salvar categoria');
    }
  };
  const excluirCategoria = (cat: TreinamentoCategoria) =>
    confirmar(`Excluir a categoria "${cat.nome}" e todos os cursos dentro dela?`, async () => {
      try {
        await treinamentoCategoriaService.remove(cat.id);
        toast.success('Categoria excluída');
        carregar();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Erro ao excluir');
      }
    });

  // ─── Curso (1 passo) ───
  const abrirNovoCurso = (categoriaId?: string) => {
    setCursoForm({ titulo: '', descricao: '', categoriaId: categoriaId ?? '', novaCategoria: '' });
    setCursoDialog({ open: true });
  };
  const salvarCurso = async () => {
    if (!cursoForm.titulo.trim()) return toast.error('Informe o título do curso');
    setSalvandoCurso(true);
    try {
      // edição: só atualiza título/descrição
      if (cursoDialog.edit) {
        await treinamentoCursoService.update(cursoDialog.edit.id, {
          titulo: cursoForm.titulo.trim(),
          descricao: cursoForm.descricao,
        });
        setCursoDialog({ open: false });
        toast.success('Curso atualizado');
        carregar();
        return;
      }

      // criação: resolve categoria (cria na hora se for nova)
      let categoriaId = cursoForm.categoriaId;
      if (!categoriaId && cursoForm.novaCategoria.trim()) {
        const cat = await treinamentoCategoriaService.create({ nome: cursoForm.novaCategoria.trim() });
        categoriaId = cat.id;
      }
      if (!categoriaId) {
        setSalvandoCurso(false);
        return toast.error('Escolha ou crie uma categoria');
      }
      const curso = await treinamentoCursoService.create({
        categoria_id: categoriaId,
        titulo: cursoForm.titulo.trim(),
        descricao: cursoForm.descricao,
      });
      // módulo inicial automático → já leva pro builder
      await treinamentoModuloService.create(curso.id, { titulo: 'Módulo 1', ordem: 0 }).catch(() => {});
      setCursoDialog({ open: false });
      toast.success('Curso criado! Adicione os vídeos.');
      navigate(`/treinamentos/curso/${curso.id}/editar`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao salvar curso');
    } finally {
      setSalvandoCurso(false);
    }
  };

  const togglePublicar = async (curso: TreinamentoCurso) => {
    try {
      await treinamentoCursoService.update(curso.id, { status: curso.status === 'publicado' ? 'rascunho' : 'publicado' });
      carregar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao alterar status');
    }
  };
  const excluirCurso = (curso: TreinamentoCurso) =>
    confirmar(`Excluir o curso "${curso.titulo}" com seus módulos, aulas e vídeos?`, async () => {
      try {
        await treinamentoCursoService.remove(curso.id);
        toast.success('Curso excluído');
        carregar();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Erro ao excluir');
      }
    });

  const enviarCapa = async (cursoId: string, file: File) => {
    try {
      setCapaUploading(cursoId);
      await treinamentoCursoService.uploadCapa(cursoId, file);
      toast.success('Capa atualizada');
      carregar();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao enviar capa');
    } finally {
      setCapaUploading(null);
    }
  };

  const carregarRelatorio = async (cursoId: string) => {
    try {
      setRelatorio(await treinamentoCursoService.relatorio(cursoId));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao gerar relatório');
    }
  };

  const exportarRelatorioCSV = async (rel: TreinamentoRelatorio) => {
    const sep = ';';
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const cabecalho = ['Usuário', 'E-mail', 'Aulas concluídas', 'Total de aulas', 'Progresso (%)', 'Certificado'];
    const linhas = rel.linhas.map((l) =>
      [l.usuario.nome, l.usuario.email, l.concluidas, l.total, l.percent, l.certificado ?? ''].map(esc).join(sep),
    );
    // BOM (﻿) garante acentuação correta ao abrir no Excel
    const csv = '﻿' + [cabecalho.map(esc).join(sep), ...linhas].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const nome = rel.curso.titulo.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
    await saveOrShareBlob(blob, `relatorio-${nome || 'curso'}.csv`);
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treinamentos')}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
            </Button>
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-bold">Gerenciar treinamentos</h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => abrirCatDialog()}>
              <FolderPlus className="mr-2 h-4 w-4" /> Categoria
            </Button>
            <Button onClick={() => abrirNovoCurso()}>
              <Plus className="mr-2 h-4 w-4" /> Novo curso
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Carregando…</p>
        ) : categorias.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center text-muted-foreground">
              <p>Nenhum conteúdo ainda.</p>
              <Button onClick={() => abrirNovoCurso()}>
                <Plus className="mr-2 h-4 w-4" /> Criar primeiro curso
              </Button>
            </CardContent>
          </Card>
        ) : (
          categorias.map((cat) => (
            <Card key={cat.id}>
              <CardContent className="space-y-4 p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold">{cat.nome}</h2>
                    {cat.empresa_id === null && (
                      <Badge variant="secondary" className="text-[10px]">
                        <Globe className="mr-1 h-3 w-3" /> Global
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => abrirNovoCurso(cat.id)}>
                      <Plus className="mr-1 h-4 w-4" /> Curso
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => abrirCatDialog(cat)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => excluirCategoria(cat)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>

                {(cat.cursos?.length ?? 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem cursos nesta categoria.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {cat.cursos!.map((curso) => (
                      <div key={curso.id} className="group flex flex-col overflow-hidden rounded-xl border bg-card transition-shadow hover:shadow-md">
                        {/* capa clicável */}
                        <button
                          className="relative flex aspect-video w-full items-center justify-center overflow-hidden bg-gradient-to-br from-primary/70 to-primary"
                          onClick={() => navigate(`/treinamentos/curso/${curso.id}/editar`)}
                          title="Abrir editor do curso"
                        >
                          {curso.capa_url ? (
                            <img src={curso.capa_url} alt={curso.titulo} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                          ) : (
                            <GraduationCap className="h-10 w-10 text-primary-foreground/70" />
                          )}
                          <Badge
                            variant={curso.status === 'publicado' ? 'default' : 'secondary'}
                            className="absolute left-2 top-2 text-[10px] shadow-sm"
                          >
                            {curso.status === 'publicado' ? 'Publicado' : 'Rascunho'}
                          </Badge>
                          <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-sm font-medium text-white opacity-0 transition-all group-hover:bg-black/35 group-hover:opacity-100">
                            <PencilRuler className="mr-1.5 h-4 w-4" /> Editar
                          </span>
                        </button>

                        {/* corpo */}
                        <div className="flex flex-1 flex-col gap-3 p-3">
                          <p className="line-clamp-1 font-medium leading-tight" title={curso.titulo}>{curso.titulo}</p>

                          <input
                            ref={(el) => (capaInputs.current[curso.id] = el)}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) enviarCapa(curso.id, f);
                              e.target.value = '';
                            }}
                          />

                          {/* ações — uma linha só, clean */}
                          <div className="mt-auto flex items-center justify-between border-t pt-2">
                            <div className="flex items-center gap-1">
                              {curso.status === 'publicado' ? (
                                <Button variant="ghost" size="icon" className="h-8 w-8" title="Despublicar" onClick={() => togglePublicar(curso)}>
                                  <EyeOff className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary hover:text-primary" title="Publicar" onClick={() => togglePublicar(curso)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Trocar capa" disabled={capaUploading === curso.id} onClick={() => capaInputs.current[curso.id]?.click()}>
                                {capaUploading === curso.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Relatório de progresso" onClick={() => carregarRelatorio(curso.id)}>
                                <BarChart3 className="h-4 w-4" />
                              </Button>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Excluir curso" onClick={() => excluirCurso(curso)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Dialog Categoria */}
      <Dialog open={catDialog.open} onOpenChange={(o) => setCatDialog({ open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{catDialog.edit ? 'Editar categoria' : 'Nova categoria'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input value={catNome} onChange={(e) => setCatNome(e.target.value)} placeholder="Ex: Vendas" />
            </div>
            {isSuperAdmin && !catDialog.edit && (
              <>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label>Categoria global</Label>
                    <p className="text-xs text-muted-foreground">Visível para todas as empresas</p>
                  </div>
                  <Switch checked={catGlobal} onCheckedChange={setCatGlobal} />
                </div>
                {!catGlobal && (
                  <div>
                    <Label>Empresa</Label>
                    <Select value={catEmpresaId} onValueChange={setCatEmpresaId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {empresas.map((e) => (
                          <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-muted-foreground">Sem seleção, usa sua empresa ativa.</p>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog({ open: false })}>Cancelar</Button>
            <Button onClick={salvarCategoria}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Curso (1 passo) */}
      <Dialog open={cursoDialog.open} onOpenChange={(o) => setCursoDialog({ open: o })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{cursoDialog.edit ? 'Editar curso' : 'Novo curso'}</DialogTitle>
            {!cursoDialog.edit && (
              <DialogDescription>Crie o curso e já comece a adicionar os vídeos.</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input
                autoFocus
                value={cursoForm.titulo}
                onChange={(e) => setCursoForm((f) => ({ ...f, titulo: e.target.value }))}
                placeholder="Ex: Treinamento de vendas"
              />
            </div>
            {!cursoDialog.edit && (
              <div>
                <Label>Categoria</Label>
                <CategoriaCombobox
                  categorias={categorias}
                  value={cursoForm.categoriaId}
                  novaCategoria={cursoForm.novaCategoria}
                  onSelect={(idCat) => setCursoForm((f) => ({ ...f, categoriaId: idCat, novaCategoria: '' }))}
                  onCreateNew={(nome) => setCursoForm((f) => ({ ...f, novaCategoria: nome, categoriaId: '' }))}
                />
                <p className="mt-1 text-xs text-muted-foreground">Escolha uma existente ou digite para criar uma nova.</p>
              </div>
            )}
            <div>
              <Label>Descrição (opcional)</Label>
              <Textarea
                rows={3}
                value={cursoForm.descricao}
                onChange={(e) => setCursoForm((f) => ({ ...f, descricao: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCursoDialog({ open: false })}>Cancelar</Button>
            <Button onClick={salvarCurso} disabled={salvandoCurso}>
              {salvandoCurso && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {cursoDialog.edit ? 'Salvar' : 'Criar e adicionar vídeos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Relatório */}
      <Dialog open={!!relatorio} onOpenChange={(o) => !o && setRelatorio(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>Relatório — {relatorio?.curso.titulo}</DialogTitle>
                <DialogDescription>Progresso da equipe neste curso</DialogDescription>
              </div>
              {relatorio && relatorio.linhas.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => exportarRelatorioCSV(relatorio)}>
                  <Download className="mr-1 h-4 w-4" /> Exportar CSV
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {(relatorio?.linhas ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Sem usuários no escopo.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background text-left text-muted-foreground">
                  <tr>
                    <th className="py-2">Usuário</th>
                    <th className="py-2">Progresso</th>
                    <th className="py-2">Certificado</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorio?.linhas.map((l) => (
                    <tr key={l.usuario.id} className="border-t">
                      <td className="py-2">
                        <div className="font-medium">{l.usuario.nome}</div>
                        <div className="text-xs text-muted-foreground">{l.usuario.email}</div>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <Progress value={l.percent} className="h-2 w-24" />
                          <span className="text-xs">{l.concluidas}/{l.total}</span>
                        </div>
                      </td>
                      <td className="py-2">
                        {l.certificado ? (
                          <Badge className="bg-green-600 text-[10px] hover:bg-green-600">{l.certificado}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmState}
        message={confirmState?.msg ?? ''}
        onOpenChange={(o) => !o && fecharConfirm()}
        onConfirm={() => { confirmState?.action(); fecharConfirm(); }}
      />
    </AppLayout>
  );
}

// ─── Combobox de categoria (escolher existente ou criar na hora) ────────────────

function CategoriaCombobox({
  categorias,
  value,
  novaCategoria,
  onSelect,
  onCreateNew,
}: {
  categorias: TreinamentoCategoria[];
  value: string;
  novaCategoria: string;
  onSelect: (id: string) => void;
  onCreateNew: (nome: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selecionada = categorias.find((c) => c.id === value);
  const label = selecionada ? selecionada.nome : novaCategoria ? `Criar: ${novaCategoria}` : 'Selecione ou crie…';
  const jaExiste = (q: string) => categorias.some((c) => c.nome.toLowerCase() === q.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
          <span className={selecionada || novaCategoria ? '' : 'text-muted-foreground'}>{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar ou criar categoria…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>
              {query.trim() ? (
                <button
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent"
                  onClick={() => { onCreateNew(query.trim()); setQuery(''); setOpen(false); }}
                >
                  <Plus className="h-4 w-4" /> Criar "{query.trim()}"
                </button>
              ) : (
                'Digite para buscar ou criar'
              )}
            </CommandEmpty>
            <CommandGroup>
              {categorias.map((c) => (
                <CommandItem key={c.id} value={c.nome} onSelect={() => { onSelect(c.id); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === c.id ? 'opacity-100' : 'opacity-0'}`} />
                  {c.nome}
                  {c.empresa_id === null && <Badge variant="secondary" className="ml-auto text-[10px]">Global</Badge>}
                </CommandItem>
              ))}
              {query.trim() && !jaExiste(query) && (
                <CommandItem value={`__criar_${query}`} onSelect={() => { onCreateNew(query.trim()); setQuery(''); setOpen(false); }}>
                  <Plus className="mr-2 h-4 w-4" /> Criar "{query.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
